import { DocumentSections, Entity } from '../../models';

const withSectionValuesAt = <T extends Entity>(
  sections: DocumentSections<T> | undefined,
  index: number,
  updater: (arr: T[]) => T[],
): DocumentSections<T> => {
  const anyS = sections as any;

  // Case 1: { sections: T[][] }
  if (anyS && Array.isArray(anyS.sections)) {
    const nextSections = [...anyS.sections];
    const prev = nextSections[index] ?? [];
    nextSections[index] = updater(prev);
    // keep other props intact
    return { ...anyS, sections: nextSections };
  }

  // Case 2: plain object with numeric keys
  const prev = anyS?.[index] ?? [];
  return { ...anyS, [index]: updater(prev) };
};

// Generic helpers for { [iri: string]: T[] }
const setSectionValues = <T extends Entity>(
  sections: DocumentSections<T> | undefined,
  iri: string,
  values: T[],
): DocumentSections<T> => {
  return { ...(sections ?? {}), [iri]: values };
};

const updateSectionValues = <T extends Entity>(
  sections: DocumentSections<T> | undefined,
  iri: string,
  updater: (existing: T[]) => T[],
): DocumentSections<T> => {
  const cur = sections?.[iri] ?? [];
  return { ...(sections ?? {}), [iri]: updater(cur) };
};

const removeSection = <T extends Entity>(
  sections: DocumentSections<T> | undefined,
  iri: string,
): DocumentSections<T> => {
  const { [iri]: _, ...rest } = sections ?? {};
  return rest as DocumentSections<T>;
};

const replaceByIri = <T extends { ['@id']?: string }>(list: T[] | undefined, next: T): T[] => {
  const iri = next['@id'];
  if (!iri) return list ? [...list] : [next];

  const src = list ?? [];
  const idx = src.findIndex((s) => s['@id'] === iri);
  if (idx === -1) {
    // not present → append (or prepend; your choice)
    return [...src, next];
  }
  // present → replace in place (immutable)
  const out = [...src];
  out[idx] = next;
  return out;
};

export { withSectionValuesAt, setSectionValues, updateSectionValues, removeSection, replaceByIri };
